import React, { FC, PropsWithChildren, useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../constants/colors';
import plusIcon from '../../assets/images/icons/plus.svg';

const TRANSITION_TIME = '500ms';

export const FAQItem: FC<
  PropsWithChildren<{
    title: string;
    isOpened: boolean;
    onClick: () => void;
  }>
> = ({ title, isOpened, onClick, children }) => {
  const ref = useRef<HTMLDivElement>();

  useLayoutEffect(() => {
    if (ref.current) {
      if (!isOpened) {
        const { height } = window.getComputedStyle(ref.current);
        ref.current.style.height = height;
        requestAnimationFrame(() => {
          ref.current.style.height = '0';
        });
      } else {
        ref.current.style.height = 'auto';
        requestAnimationFrame(() => {
          const { height } = window.getComputedStyle(ref.current);
          ref.current.style.height = '0';
          requestAnimationFrame(() => {
            ref.current.style.height = height;
          });
        });
      }
    }
  }, [isOpened]);

  return (
    <StyledFAQItem>
      <StyledHeader isOpened={isOpened} onClick={onClick}>
        {title}
        <StyledPlusIcon isOpened={isOpened} />
      </StyledHeader>
      <StyledBodyWrapper ref={ref} isOpened={isOpened}>
        <StyledBody>{children}</StyledBody>
      </StyledBodyWrapper>
    </StyledFAQItem>
  );
};

const StyledFAQItem = styled.div`
  background-color: ${colors.white};
  width: 100%;
  max-width: 1200px;
  margin: auto;
  border-radius: 16px;
  overflow: hidden;
  font-size: 18px;
`;

const HighlightedHeaderStyle = css`
  color: ${colors.orange};
`;

const StyledHeader = styled.div<{ isOpened: boolean }>`
  min-height: 80px;
  padding: 0 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: bold;
  color: ${colors.darkGray};
  transition: color 200ms;

  ${({ isOpened }) => isOpened && HighlightedHeaderStyle};

  &:hover {
    ${HighlightedHeaderStyle};
  }
`;

const StyledBodyWrapper = styled.div<{ isOpened: boolean }>`
  transition: height ${TRANSITION_TIME} ease-out;
  overflow: hidden;
  text-align: left;
`;

const StyledBody = styled.div`
  padding: 30px;
`;

const StyledPlusIcon = styled.div<{ isOpened: boolean }>`
  background-image: url('${plusIcon}');
  background-size: contain;
  pointer-events: none;
  flex-shrink: 0;
  flex-grow: 0;
  width: 24px;
  height: 24px;
  transition: transform ${TRANSITION_TIME} ease-out;

  ${({ isOpened }) =>
    isOpened &&
    css`
      transform: rotate(45deg);
    `};
`;
